import React, { memo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { getGiftProduct, getGiftHashId } from '../../../store/data/data.selectors';
import { getIsActivate, getIsPreview } from '../../../store/statuses/statuses.selectors';
import useGiftActions from '../../../hooks/useGiftActions';
import GiftProductPreview from '../GiftProductPreview';
import { detectCountryRequest } from '../../../../../store/geoIpCountry/geoIpCountry.actions';
import AlycePrivacy from '../../../../../components/AlycePrivacy/AlycePrivacy';
import { getCanDeclineGift, getGiftActionFlow } from '../../../store/recipient.selectors';
import { GiftActionFlow } from '../../../../../store/types/GiftActionFlow.types';
import { TUseAcceptPolicyHandler } from '../../../../../hooks/useAcceptPolicy';
import { IAlyceCustomTheme } from '../../../../../styles/alyceTheme';
import { getIsLoaded } from '../../../../../store/geoIpCountry/geoIpCountry.selectors';

import GiftCountryFlow from './GiftCountryFlow';
import DeclineLink from './DeclineLink';
import CampaignCountryFlow from './CampaignCountryFlow';
import WrongCountryFlow from './WrongCountryFlow';
import FlowSkeleton from './FlowSkeleton';

const useStyles = makeStyles<IAlyceCustomTheme>(({ breakpoints, spacing }) => ({
  giftActions: {
    paddingTop: spacing(3),
    [breakpoints.down('lg')]: {
      paddingTop: spacing(6),
    },
  },
}));

export interface IRecipientGiftActionsProps {
  onGiftDetailsView: () => void;
  onAcceptPolicy: TUseAcceptPolicyHandler;
  parentUrl: string;
}

const RecipientGiftActions = ({
  onGiftDetailsView,
  onAcceptPolicy,
  parentUrl,
}: IRecipientGiftActionsProps): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const sentProduct = useSelector(getGiftProduct);
  const hashId = useSelector(getGiftHashId);
  const isPreview = useSelector(getIsPreview);
  const canDecline = useSelector(getCanDeclineGift);
  const giftActionFlow = useSelector(getGiftActionFlow);
  const isGeoIpLoaded = useSelector(getIsLoaded);
  const isActivate = useSelector(getIsActivate);

  const { handleAccept, handleDonate, handleExchange } = useGiftActions(parentUrl);

  const handleAcceptProduct = useCallback(() => {
    if (sentProduct.hasOptions) {
      onGiftDetailsView();
      return;
    }
    handleAccept({ ...sentProduct, fromMarketplace: false });
    onAcceptPolicy('Gift invitation - Pressed accept CTA', { product: sentProduct });
  }, [handleAccept, sentProduct, onGiftDetailsView, onAcceptPolicy]);

  const handleExchangeProduct = useCallback(
    useDonation => {
      handleExchange(useDonation);
      onAcceptPolicy('Gift invitation - Pressed exchange CTA', { product: sentProduct });
    },
    [handleExchange, sentProduct, onAcceptPolicy],
  );

  const handleDonateProduct = useCallback(() => {
    handleDonate();
    onAcceptPolicy('Gift invitation - Pressed change charity CTA', { product: sentProduct });
  }, [handleDonate, sentProduct, onAcceptPolicy]);

  useEffect(() => {
    if (hashId && !isPreview && !isGeoIpLoaded && !isActivate) {
      dispatch(detectCountryRequest({ hashId }));
    }
  }, [hashId, isPreview, isGeoIpLoaded, isActivate, dispatch]);

  return (
    <Box position="relative" width={1} maxWidth={500} minHeight="calc(100vh - 85px)" mt={0} mb={2} mx="auto">
      <Grid container direction="row">
        <Grid className={classes.giftActions} item xs={12}>
          <GiftProductPreview />
          {giftActionFlow === GiftActionFlow.loading && <FlowSkeleton />}
          {giftActionFlow === GiftActionFlow.giftCountry && (
            <GiftCountryFlow
              onAccept={handleAcceptProduct}
              onExchange={handleExchangeProduct}
              onDonate={handleDonateProduct}
            />
          )}
          {giftActionFlow === GiftActionFlow.campaignCountry && (
            <CampaignCountryFlow onExchange={handleExchangeProduct} onDonate={handleDonateProduct} />
          )}
          {giftActionFlow === GiftActionFlow.wrongCountry && <WrongCountryFlow onDonate={handleDonateProduct} />}
          <AlycePrivacy />
          {canDecline && <DeclineLink href={`${parentUrl}/decline`} />}
        </Grid>
      </Grid>
    </Box>
  );
};

export default memo<IRecipientGiftActionsProps>(RecipientGiftActions);
